import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

// hooks
import useMediaRecorder from 'shiftNotes/hooks/useMediaRecorder';
import {
  AddShiftNoteData,
  useAddShiftNoteMutation,
  useExtractEntitiesMutation,
  useGenerateInstantReflexMutation,
} from 'shared/hooks/api/shiftNotes';
import { useAnswerGuidanceQuestionMutation } from 'priorities/hooks/useGuidanceQuestions';
import useShiftNoteUpdates from 'shiftNotes/hooks/useShiftNoteUpdates';
import { APIKey } from 'constants/apiKeys';

// types
import { Note } from 'shiftNotes/_types';
import { Event } from 'events/_types';
import { PaginatedResponse } from 'dashboard/types';

export type NoteInputType = 'microphone' | 'text';

const sessionCreatedNoteIds = new Set<string>();

export function wasNoteCreatedThisSession(noteId: string): boolean {
  return sessionCreatedNoteIds.has(noteId);
}

function withResolveFlag(data: AddShiftNoteData): AddShiftNoteData {
  if (!(data instanceof FormData)) return { ...data, resolve: true };
  data.append('resolve', 'true');
  return data;
}

export type PendingUpload = {
  id: number;
  status: 'pending' | 'error';
  error?: Error;
  data: AddShiftNoteData;
  guidanceQuestionId: string | null;
  inputType: NoteInputType;
  createdAt: number;
  createdNoteIds?: string[];
};

export type ShiftNoteCaptureContextValue = {
  isRecording: boolean;
  isRecordingPaused: boolean;
  onRecordingToggle: (guidanceQuestionId?: string) => void;
  onCancelRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  submitRecording: () => void;
  mediaError: Error | null;
  showCreatedCard: boolean;
  createdNotes: Note[];
  closeCreatedCard: () => void;
  submitTextNote: (text: string) => void;
  submitAttachmentsOnly: () => void;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  clearAttachments: () => void;
  audioLevel: number;
  lastInputType: NoteInputType;
  isWritingMode: boolean;
  writingModeInitialText: string;
  openWritingMode: (guidanceQuestionId?: string) => void;
  closeWritingMode: () => void;
  isResolveMode: boolean;
  enterResolveMode: () => void;
  exitResolveMode: () => void;
  clearActiveGuidanceQuestion: () => void;
  pendingUploads: PendingUpload[];
  retryUpload: (id: number) => void;
  dismissUpload: (id: number) => void;
  markPendingSilent: () => void;
};

export const ShiftNoteCaptureContext =
  createContext<ShiftNoteCaptureContextValue | null>(null);

export function useShiftNoteCapture(): ShiftNoteCaptureContextValue {
  const ctx = useContext(ShiftNoteCaptureContext);
  if (!ctx) {
    throw new Error(
      'useShiftNoteCapture must be used within ShiftNoteCaptureProvider'
    );
  }
  return ctx;
}

export function ShiftNoteCaptureProvider({
  children,
  onNotesCreated,
  eventId,
}: {
  children: React.ReactNode;
  onNotesCreated?: (notes: Note[]) => void;
  eventId?: string;
}): JSX.Element {
  const shiftNotesCapture = usePosthogCapture();
  const queryClient = useQueryClient();
  const [showCreatedCard, setShowCreatedCard] = useState(false);
  const [lastInputType, setLastInputType] = useState<NoteInputType>('text');
  const [createdNotes, setCreatedNotes] = useState<Note[]>([]);
  const [isWritingMode, setIsWritingMode] = useState(false);
  const [writingModeInitialText, setWritingModeInitialText] = useState('');
  const [isResolveMode, setIsResolveMode] = useState(false);
  const isResolveModeRef = useRef(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const nextPendingIdRef = useRef(0);
  const activeGuidanceQuestionIdRef = useRef<string | null>(null);
  const silentUploadIdsRef = useRef<Set<number>>(new Set());

  useShiftNoteUpdates();

  const { mutate: generateInstantReflex } = useGenerateInstantReflexMutation();

  const { mutate: extractEntities } = useExtractEntitiesMutation({
    onSuccess: (updatedNote) => {
      setCreatedNotes((prev) =>
        prev.map((note) => (note.id === updatedNote.id ? updatedNote : note))
      );
    },
  });

  const handleNotesCreated = useCallback(
    (notes: Note[], options: { silent?: boolean } = {}) => {
      activeGuidanceQuestionIdRef.current = null;
      if (!options.silent) {
        setShowCreatedCard(true);
        setCreatedNotes(notes);
      }
      notes.forEach((note) => sessionCreatedNoteIds.add(note.id));
      onNotesCreated?.(notes);
      notes.forEach((note) => {
        extractEntities(note.id, {
          onSuccess: () => {
            if (!note.instant_reflex) {
              generateInstantReflex(note.id);
            }
          },
        });
      });
    },
    [extractEntities, generateInstantReflex, onNotesCreated]
  );

  const { mutateAsync: uploadShiftNote } = useAddShiftNoteMutation({ eventId });

  const { mutateAsync: answerGuidanceQuestion } =
    useAnswerGuidanceQuestionMutation();

  const runUpload = useCallback(
    (upload: PendingUpload) => {
      const promise = upload.guidanceQuestionId
        ? answerGuidanceQuestion({
            guidanceQuestionId: upload.guidanceQuestionId,
            data: upload.data,
          }).then((note) => [note])
        : uploadShiftNote(upload.data);
      promise.then(
        (notes) => {
          shiftNotesCapture.captureCreatedNote({
            input_type: upload.inputType,
          });
          const silent = silentUploadIdsRef.current.has(upload.id);
          if (silent) silentUploadIdsRef.current.delete(upload.id);
          handleNotesCreated(notes || [], { silent });

          const createdIds = (notes || []).map((n) => n.id);

          const removePending = () =>
            setPendingUploads((prev) => prev.filter((u) => u.id !== upload.id));

          if (createdIds.length === 0) {
            removePending();
            return;
          }

          // Stamp the pending upload with its created note IDs so consumers can
          // decide when to stop rendering it in place of the real event.
          setPendingUploads((prev) =>
            prev.map((u) =>
              u.id === upload.id ? { ...u, createdNoteIds: createdIds } : u
            )
          );

          const notePresentInCache = () => {
            const eventLists = queryClient.getQueriesData<
              PaginatedResponse<Event>
            >({ queryKey: [APIKey.EVENT, 'list'] });
            return eventLists.some(([, data]) => {
              if (!data?.results) return false;
              const eventNoteIds = new Set(
                data.results.flatMap((e) =>
                  (e.shift_notes || []).map((n) => n.id)
                )
              );
              return createdIds.every((id) => eventNoteIds.has(id));
            });
          };

          const listsSettled = () =>
            queryClient.isFetching({ queryKey: [APIKey.EVENT] }) === 0;

          const readyToSwap = () => notePresentInCache() && listsSettled();

          if (readyToSwap()) {
            removePending();
            return;
          }

          let safetyTimeout: ReturnType<typeof setTimeout>;
          const unsubscribe = queryClient.getQueryCache().subscribe(() => {
            if (readyToSwap()) {
              unsubscribe();
              clearTimeout(safetyTimeout);
              removePending();
            }
          });

          safetyTimeout = setTimeout(() => {
            unsubscribe();
            removePending();
          }, 10_000);
        },
        (error: Error) => {
          setPendingUploads((prev) =>
            prev.map((u) =>
              u.id === upload.id ? { ...u, status: 'error', error } : u
            )
          );
          silentUploadIdsRef.current.delete(upload.id);
          shiftNotesCapture.captureReceivedError({
            input_type: upload.inputType,
            error_type: error?.message || '',
          });
        }
      );
    },
    [
      answerGuidanceQuestion,
      shiftNotesCapture,
      uploadShiftNote,
      handleNotesCreated,
      queryClient,
    ]
  );

  const markPendingSilent = useCallback(() => {
    pendingUploads.forEach((u) => {
      if (u.status === 'pending') {
        silentUploadIdsRef.current.add(u.id);
      }
    });
  }, [pendingUploads]);

  const setResolveMode = useCallback((enabled: boolean) => {
    isResolveModeRef.current = enabled;
    setIsResolveMode(enabled);
  }, []);

  const exitResolveMode = useCallback(
    () => setResolveMode(false),
    [setResolveMode]
  );

  const startUpload = useCallback(
    (data: AddShiftNoteData, inputType: NoteInputType) => {
      const resolve = isResolveModeRef.current;
      if (resolve) setResolveMode(false);
      nextPendingIdRef.current += 1;
      const upload: PendingUpload = {
        id: nextPendingIdRef.current,
        status: 'pending',
        data: resolve ? withResolveFlag(data) : data,
        guidanceQuestionId: activeGuidanceQuestionIdRef.current,
        inputType,
        createdAt: Date.now(),
      };
      setPendingUploads((prev) => [...prev, upload]);
      runUpload(upload);
    },
    [runUpload, setResolveMode]
  );

  const retryUpload = useCallback(
    (id: number) => {
      const target = pendingUploads.find((u) => u.id === id);
      if (!target) return;

      shiftNotesCapture.captureErrorRetried({ input_type: target.inputType });

      if (!(target.data instanceof FormData)) {
        setPendingUploads((prev) => prev.filter((u) => u.id !== id));
        setShowCreatedCard(false);
        setCreatedNotes([]);
        setWritingModeInitialText(target.data.text_note);
        setResolveMode(!!target.data.resolve);
        activeGuidanceQuestionIdRef.current = target.guidanceQuestionId;
        setIsWritingMode(true);
        return;
      }

      const retried: PendingUpload = {
        ...target,
        status: 'pending',
        error: undefined,
      };
      setPendingUploads((prev) => prev.map((u) => (u.id === id ? retried : u)));
      runUpload(retried);
    },
    [pendingUploads, runUpload, setResolveMode, shiftNotesCapture]
  );

  const dismissUpload = useCallback(
    (id: number) => {
      setPendingUploads((prev) => {
        const target = prev.find((u) => u.id === id);
        if (target) {
          shiftNotesCapture.captureErrorDismissed({
            input_type: target.inputType,
          });
        }
        return prev.filter((u) => u.id !== id);
      });
    },
    [shiftNotesCapture]
  );

  const pendingAttachmentsRef = useRef<{
    images: File[];
    video: File | null;
  }>({ images: [], video: null });

  const {
    startRecording,
    stopRecording,
    cancelRecording,
    pauseRecording,
    resumeRecording,
    isRecording,
    isPaused: isRecordingPaused,
    error: mediaError,
    audioLevel,
  } = useMediaRecorder({
    onData: (data: Blob) => {
      setLastInputType('microphone');
      const formData = new FormData();
      formData.append('audio_file', data);
      const { images, video } = pendingAttachmentsRef.current;
      images.forEach((file) => formData.append('image_files', file));
      if (video) formData.append('video_file', video);
      pendingAttachmentsRef.current = { images: [], video: null };
      startUpload(formData, 'microphone');
    },
  });

  const onRecordingToggle = useCallback(
    (guidanceQuestionId?: string) => {
      if (isRecording) {
        stopRecording();
        return;
      }
      if (guidanceQuestionId !== undefined) {
        activeGuidanceQuestionIdRef.current = guidanceQuestionId;
      }
      startRecording();
    },
    [isRecording, startRecording, stopRecording]
  );

  const onCancelRecording = useCallback(() => {
    pendingAttachmentsRef.current = { images: [], video: null };
    cancelRecording();
  }, [cancelRecording]);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const submitRecording = useCallback(() => {
    const images: File[] = [];
    let video: File | null = null;
    attachments.forEach((file) => {
      if (file.type.startsWith('video/')) {
        video = file;
      } else {
        images.push(file);
      }
    });
    pendingAttachmentsRef.current = { images, video };
    clearAttachments();
    stopRecording();
  }, [attachments, clearAttachments, stopRecording]);

  const prevEventIdRef = useRef(eventId);
  useEffect(() => {
    if (prevEventIdRef.current === eventId) return;
    prevEventIdRef.current = eventId;
    setIsWritingMode(false);
    setWritingModeInitialText('');
    setResolveMode(false);
    setShowCreatedCard(false);
    setCreatedNotes([]);
    activeGuidanceQuestionIdRef.current = null;
    clearAttachments();
    pendingAttachmentsRef.current = { images: [], video: null };
    if (isRecording) {
      cancelRecording();
    }
  }, [eventId, isRecording, cancelRecording, clearAttachments, setResolveMode]);

  const closeCreatedCard = useCallback(() => {
    setShowCreatedCard(false);
    setCreatedNotes([]);
  }, []);

  const openWritingMode = useCallback((guidanceQuestionId?: string) => {
    setShowCreatedCard(false);
    setCreatedNotes([]);
    setWritingModeInitialText('');
    if (guidanceQuestionId !== undefined) {
      activeGuidanceQuestionIdRef.current = guidanceQuestionId;
    }
    setIsWritingMode(true);
  }, []);

  const enterResolveMode = useCallback(() => {
    activeGuidanceQuestionIdRef.current = null;
    setResolveMode(true);
  }, [setResolveMode]);

  const closeWritingMode = useCallback(() => {
    setIsWritingMode(false);
    setWritingModeInitialText('');
  }, []);

  const clearActiveGuidanceQuestion = useCallback(() => {
    activeGuidanceQuestionIdRef.current = null;
  }, []);

  const submitTextNote = useCallback(
    (text: string) => {
      setLastInputType('text');
      if (attachments.length === 0) {
        startUpload({ text_note: text }, 'text');
        clearAttachments();
        return;
      }
      const formData = new FormData();
      if (text) formData.append('text_note', text);
      attachments.forEach((file) => {
        if (file.type.startsWith('video/')) {
          formData.append('video_file', file);
        } else {
          formData.append('image_files', file);
        }
      });
      startUpload(formData, 'text');
      clearAttachments();
    },
    [attachments, clearAttachments, startUpload]
  );

  const submitAttachmentsOnly = useCallback(() => {
    if (attachments.length === 0) return;
    submitTextNote('');
  }, [attachments.length, submitTextNote]);

  const value = useMemo(
    () => ({
      isRecording,
      isRecordingPaused,
      onRecordingToggle,
      onCancelRecording,
      pauseRecording,
      resumeRecording,
      submitRecording,
      mediaError,
      showCreatedCard,
      createdNotes,
      closeCreatedCard,
      submitTextNote,
      submitAttachmentsOnly,
      attachments,
      setAttachments,
      clearAttachments,
      audioLevel,
      lastInputType,
      isWritingMode,
      writingModeInitialText,
      openWritingMode,
      closeWritingMode,
      isResolveMode,
      enterResolveMode,
      exitResolveMode,
      clearActiveGuidanceQuestion,
      pendingUploads,
      retryUpload,
      dismissUpload,
      markPendingSilent,
    }),
    [
      isRecording,
      isRecordingPaused,
      onRecordingToggle,
      onCancelRecording,
      pauseRecording,
      resumeRecording,
      submitRecording,
      mediaError,
      showCreatedCard,
      createdNotes,
      closeCreatedCard,
      submitTextNote,
      submitAttachmentsOnly,
      attachments,
      clearAttachments,
      audioLevel,
      lastInputType,
      isWritingMode,
      writingModeInitialText,
      openWritingMode,
      closeWritingMode,
      isResolveMode,
      enterResolveMode,
      exitResolveMode,
      clearActiveGuidanceQuestion,
      pendingUploads,
      retryUpload,
      dismissUpload,
      markPendingSilent,
    ]
  );

  return (
    <ShiftNoteCaptureContext.Provider value={value}>
      {children}
    </ShiftNoteCaptureContext.Provider>
  );
}
